/**
 * Veluna PDP Mystery Popup — full-screen scratch-to-reveal discount.
 * Honest by design:
 *  - Prizes are real, weighted random; visitor genuinely receives what they reveal.
 *  - No fabricated social proof, no fake countdowns, no fake stock claims.
 *  - One real reveal per visitor per cooldown window.
 * Email-gated: code reveals AFTER email capture so the lead enters lifecycle.
 * Surface: PDP only (gated by Liquid).
 */
(function () {
  'use strict';

  const cfgEl = document.getElementById('veluna-pdp-mystery-config');
  if (!cfgEl) return;
  let CFG;
  try { CFG = JSON.parse(cfgEl.textContent); } catch (_) { return; }
  if (!CFG || !CFG.product || !CFG.product.image) return;

  const STORAGE = {
    won: 'veluna_pdp_mystery_won',
    lastShown: 'veluna_pdp_mystery_last_shown',
    captured: 'veluna_email_captured'
  };

  try {
    if (/\/products\//.test(window.location.pathname)) {
      (window.__vlnPageSpecific = window.__vlnPageSpecific || []).push('pdp-mystery');
      window.VelunaCaptureGuard && window.VelunaCaptureGuard.registerPageSpecific('pdp-mystery');
    }
  } catch (e) {}

  const BACKEND = {
    url: 'https://ary09mimgg.execute-api.us-east-2.amazonaws.com/Prod/subscribe',
    sequence: 'pdp_mystery_offer',
    popupVariant: 'pdp_mystery_offer'
  };

  // Map product handle -> ritual concern
  const RITUALS = {
    mc: { id: 'mc', name: 'Mind Calm' },
    dr: { id: 'dr', name: 'Deep Restore' },
    ss: { id: 'ss', name: 'Sacred Soothe' },
    cr: { id: 'cr', name: 'Circadian Reset' }
  };

  function deriveRitual(handle) {
    const h = (handle || '').toLowerCase();
    if (h.includes('mind-calm')) return RITUALS.mc;
    if (h.includes('deep-restore')) return RITUALS.dr;
    if (h.includes('sacred-soothe')) return RITUALS.ss;
    if (h.includes('circadian-reset')) return RITUALS.cr;
    return null;
  }

  const ritual = deriveRitual(CFG.product.handle);

  // ---------- helpers ----------
  function ls(k) { try { return localStorage.getItem(k); } catch (_) { return null; } }
  function lsSet(k, v) { try { localStorage.setItem(k, v); } catch (_) {} }
  function hasWon() { return !!ls(STORAGE.won); }
  function inCooldown() {
    const last = parseInt(ls(STORAGE.lastShown) || '0', 10);
    if (!last) return false;
    // Cooldown in seconds (default 60). Backwards-compat with older themes
    // that still emit cooldownMinutes.
    const seconds = (typeof CFG.cooldownSeconds === 'number' && CFG.cooldownSeconds > 0)
      ? CFG.cooldownSeconds
      : (typeof CFG.cooldownMinutes === 'number' ? CFG.cooldownMinutes * 60 : 60);
    return (Date.now() - last) < (seconds * 1000);
  }
  function recordShown() { lsSet(STORAGE.lastShown, String(Date.now())); }

  function cartIsEmpty(cb) {
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then((r) => r.ok ? r.json() : null)
      .then((c) => cb(!c || (c.item_count || 0) === 0))
      .catch(() => cb(true));
  }

  function pickPrize() {
    const prizes = (CFG.prizes || []).filter((p) => p && p.code && p.weight > 0 && p.percent > 0);
    if (!prizes.length) return null;
    const total = prizes.reduce((s, p) => s + p.weight, 0);
    let r = Math.random() * total;
    for (const p of prizes) {
      r -= p.weight;
      if (r <= 0) return p;
    }
    return prizes[prizes.length - 1];
  }

  // ---------- state ----------
  let overlay = null;
  let isOpen = false;
  let dwellTimer = null;
  let prize = null;
  let scratched = false;
  let capturedEmail = null;

  // ---------- styles ----------
  function injectStyles() {
    if (document.getElementById('vpm-styles')) return;
    const css = `
.vpm__overlay { position: fixed; inset: 0; z-index: 999999; background: rgba(10,12,24,0.92); display: flex; align-items: center; justify-content: center; padding: 24px; opacity: 0; transition: opacity .3s ease; backdrop-filter: blur(6px); }
.vpm__overlay.is-open { opacity: 1; }
.vpm__shell { position: relative; width: min(960px, 100%); max-height: 92vh; background: linear-gradient(160deg, #1a1f35 0%, #0d1120 100%); border-radius: 18px; overflow: hidden; display: grid; grid-template-columns: 1fr 1fr; box-shadow: 0 30px 80px rgba(0,0,0,.55); transform: translateY(16px) scale(.98); transition: transform .35s cubic-bezier(.2,.8,.2,1); color: #fdfaf3; font-family: 'Inter', system-ui, -apple-system, sans-serif; }
.vpm__overlay.is-open .vpm__shell { transform: translateY(0) scale(1); }
.vpm__close { position: absolute; top: 14px; right: 14px; background: rgba(255,255,255,.08); border: 0; color: #fdfaf3; cursor: pointer; padding: 8px; border-radius: 50%; opacity: .7; z-index: 3; line-height: 0; }
.vpm__close:hover { opacity: 1; background: rgba(255,255,255,.16); }
.vpm__media { background: radial-gradient(circle at 30% 30%, #2a3050 0%, #0d1120 70%); display: flex; align-items: center; justify-content: center; min-height: 420px; padding: 32px; position: relative; overflow: hidden; }
.vpm__media::after { content: ''; position: absolute; inset: 0; background: radial-gradient(circle at 70% 80%, rgba(201,168,108,.18) 0%, transparent 50%); pointer-events: none; }
.vpm__media img { width: 88%; max-width: 100%; max-height: 380px; object-fit: contain; filter: drop-shadow(0 20px 40px rgba(0,0,0,.5)); position: relative; z-index: 1; }
.vpm__media--noimg { background: radial-gradient(circle at 50% 40%, #2a3050 0%, #0d1120 75%); }
.vpm__media-tag { position: absolute; top: 24px; left: 24px; background: rgba(201,168,108,.15); color: #c9a86c; border: 1px solid rgba(201,168,108,.4); padding: 6px 12px; border-radius: 999px; font-size: 11px; letter-spacing: .15em; text-transform: uppercase; font-weight: 600; z-index: 2; }
.vpm__body { padding: 44px 40px; display: flex; flex-direction: column; justify-content: center; gap: 14px; }
.vpm__eyebrow { color: #c9a86c; font-size: 11px; letter-spacing: .2em; text-transform: uppercase; font-weight: 600; margin: 0; }
.vpm__h { font-family: 'Playfair Display', Georgia, serif; font-size: 30px; line-height: 1.15; margin: 0; font-weight: 600; color: #fdfaf3; }
.vpm__h em { color: #c9a86c; font-style: normal; }
.vpm__sub { font-size: 14px; line-height: 1.55; color: rgba(253,250,243,.72); margin: 0; }
.vpm__product-line { font-size: 13px; color: rgba(253,250,243,.6); margin: 0; }
.vpm__scratch-wrap { position: relative; height: 130px; border-radius: 12px; overflow: hidden; border: 1px solid rgba(201,168,108,.3); margin: 6px 0 4px; background: #fdfaf3; }
.vpm__scratch-prize { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; color: #1a1f35; font-family: 'Playfair Display', Georgia, serif; padding: 8px; }
.vpm__scratch-prize-percent { font-size: 44px; font-weight: 700; line-height: 1; color: #1a1f35; }
.vpm__scratch-prize-label { font-size: 12px; letter-spacing: .15em; text-transform: uppercase; color: #6b7290; margin-top: 6px; font-family: 'Inter', sans-serif; }
.vpm__scratch-canvas { position: absolute; inset: 0; cursor: grab; touch-action: none; display: block; width: 100%; height: 100%; }
.vpm__scratch-canvas:active { cursor: grabbing; }
.vpm__hint { font-size: 12px; color: rgba(253,250,243,.5); text-align: center; margin: 0; min-height: 16px; }
.vpm__form { display: flex; flex-direction: column; gap: 10px; margin-top: 4px; }
.vpm__input { width: 100%; padding: 14px 16px; border: 1.5px solid rgba(201,168,108,.4); border-radius: 10px; font-size: 15px; background: rgba(253,250,243,.06); color: #fdfaf3; box-sizing: border-box; font-family: inherit; }
.vpm__input:focus { outline: none; border-color: #c9a86c; background: rgba(253,250,243,.1); }
.vpm__input::placeholder { color: rgba(253,250,243,.45); }
.vpm__btn { background: #c9a86c; color: #1a1f35; border: 0; padding: 16px 22px; border-radius: 10px; font-size: 15px; font-weight: 700; cursor: pointer; font-family: inherit; transition: all .15s ease; text-decoration: none; text-align: center; display: block; letter-spacing: .02em; }
.vpm__btn:hover { background: #d8b97a; transform: translateY(-1px); }
.vpm__btn[disabled], .vpm__btn--ghost { background: rgba(201,168,108,.18); color: rgba(253,250,243,.4); cursor: not-allowed; }
.vpm__btn--ghost { background: transparent; color: rgba(253,250,243,.5); border: 1px solid rgba(253,250,243,.15); padding: 10px; font-size: 12px; font-weight: 500; margin-top: 4px; }
.vpm__btn--ghost:hover { background: rgba(255,255,255,.04); color: rgba(253,250,243,.7); transform: none; }
.vpm__error { color: #ff8a8a; font-size: 12px; margin: 0; min-height: 16px; }
.vpm__legal { font-size: 11px; color: rgba(253,250,243,.45); margin: 4px 0 0; line-height: 1.5; }
@media (max-width: 760px) {
  .vpm__overlay { padding: 0; align-items: stretch; backdrop-filter: blur(8px); }
  .vpm__shell {
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
    width: 100%;
    max-width: 100%;
    min-height: 100vh;
    min-height: 100dvh;
    max-height: 100vh;
    max-height: 100dvh;
    border-radius: 0;
    overflow: hidden;
    transform: none;
  }
  .vpm__close {
    position: absolute;
    top: max(12px, env(safe-area-inset-top, 0px));
    right: 12px;
    width: 40px;
    height: 40px;
    padding: 9px;
    background: rgba(255,255,255,0.16);
    border-radius: 50%;
    z-index: 10;
    opacity: 1;
  }
  .vpm__media {
    min-height: 220px;
    padding: max(28px, calc(env(safe-area-inset-top, 0px) + 18px)) 24px 18px;
    flex-shrink: 0;
    position: relative;
  }
  .vpm__media img { max-width: 70%; max-height: 200px; }
  .vpm__media-tag {
    top: max(14px, calc(env(safe-area-inset-top, 0px) + 8px));
    left: 14px;
    font-size: 9.5px;
    padding: 4px 10px;
    letter-spacing: 0.20em;
    background: rgba(13,17,32,0.55);
    backdrop-filter: blur(6px);
    border-color: rgba(201,168,108,0.55);
    color: #e8c98a;
  }
  .vpm__body {
    padding: 18px 22px max(20px, env(safe-area-inset-bottom, 0px));
    gap: 12px;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    justify-content: flex-start;
  }
  .vpm__eyebrow { font-size: 10px; letter-spacing: 0.20em; opacity: .78; }
  .vpm__h { font-size: 24px; line-height: 1.15; letter-spacing: -0.01em; }
  .vpm__sub { font-size: 13.5px; line-height: 1.5; }
  .vpm__product-line { display: none; }
  .vpm__scratch-wrap { height: 132px; margin: 6px 0 4px; border-radius: 14px; }
  .vpm__scratch-prize-percent { font-size: 42px; }
  .vpm__scratch-prize-label { font-size: 11px; margin-top: 4px; }
  .vpm__hint { font-size: 11.5px; min-height: 14px; opacity: .7; }
  .vpm__form { gap: 10px; }
  .vpm__input { padding: 13px 14px; font-size: 16px; }
  .vpm__btn { padding: 14px 16px; font-size: 14.5px; min-height: 48px; }
  .vpm__btn--ghost { padding: 10px; min-height: 36px; font-size: 12px; margin-top: 2px; }
  .vpm__legal { font-size: 10px; line-height: 1.45; }
  .vpm__error { font-size: 11px; }
}

/* Dopamine reveal effects */
@keyframes vpm-prize-pop {
  0% { transform: scale(1); }
  35% { transform: scale(1.18); }
  70% { transform: scale(0.96); }
  100% { transform: scale(1.06); }
}
@keyframes vpm-prize-glow {
  0% { box-shadow: 0 0 0 rgba(201,168,108,0); }
  50% { box-shadow: 0 0 36px rgba(201,168,108,0.55); }
  100% { box-shadow: 0 0 22px rgba(201,168,108,0.32); }
}
.vpm__scratch-wrap.is-revealed { animation: vpm-prize-glow 1.2s ease forwards; }
.vpm__scratch-wrap.is-revealed .vpm__scratch-prize-percent { animation: vpm-prize-pop 0.7s cubic-bezier(.2,.9,.3,1.2) forwards; color: #c9a86c; }
.vpm__confetti { position: absolute; inset: 0; pointer-events: none; overflow: hidden; }
.vpm__confetti span { position: absolute; width: 8px; height: 8px; border-radius: 2px; opacity: 0; animation: vpm-confetti 1.4s cubic-bezier(.2,.7,.4,1) forwards; }
@keyframes vpm-confetti {
  0% { transform: translate(0,0) rotate(0); opacity: 1; }
  100% { transform: translate(var(--dx), var(--dy)) rotate(720deg); opacity: 0; }
}
`;
    const s = document.createElement('style');
    s.id = 'vpm-styles';
    s.textContent = css;
    document.head.appendChild(s);
  }

  // ---------- views ----------
  function renderScratchView() {
    const body = overlay.querySelector('[data-body]');
    body.innerHTML = `
      <p class="vpm__eyebrow">Hold on — one thing</p>
      <h2 class="vpm__h">Scratch to reveal <em>your</em> reward</h2>
      <p class="vpm__sub">A single discount, randomly assigned. Whatever you uncover is yours to use on this order.</p>
      <p class="vpm__product-line">For ${CFG.product.title} · ${CFG.product.price}</p>
      <div class="vpm__scratch-wrap" data-scratch-wrap>
        <div class="vpm__scratch-prize">
          <span class="vpm__scratch-prize-percent" data-prize-percent>${prize.percent}% OFF</span>
          <span class="vpm__scratch-prize-label" data-prize-label>on ${CFG.product.title}</span>
        </div>
        <canvas class="vpm__scratch-canvas" data-scratch-canvas></canvas>
      </div>
      <p class="vpm__hint" data-hint>Drag your finger across the gold panel</p>
      <button class="vpm__btn vpm__btn--ghost" data-skip type="button">No thanks, I'll pay full price</button>
    `;
    body.querySelector('[data-skip]').addEventListener('click', () => close('skip'));
    initScratch();
  }

  function renderEmailView() {
    const body = overlay.querySelector('[data-body]');
    body.innerHTML = `
      <p class="vpm__eyebrow">${prize.percent}% OFF revealed</p>
      <h2 class="vpm__h">Where should we send <em>your code</em>?</h2>
      <p class="vpm__sub">We'll email it now and apply it to your cart automatically. Plus a few short notes to help your ${ritual ? ritual.name : 'sleep'} ritual actually work.</p>
      <form class="vpm__form" data-form>
        <input class="vpm__input" type="email" name="email" placeholder="you@example.com" required autocomplete="email" />
        <button class="vpm__btn" type="submit" data-submit>Email my ${prize.percent}% off code</button>
        <p class="vpm__error" data-error></p>
      </form>
      <p class="vpm__legal">One reward per visitor. Code applies at checkout. Cannot be combined with other offers. Unsubscribe anytime.</p>
    `;
    const form = body.querySelector('[data-form]');
    form.addEventListener('submit', (e) => { e.preventDefault(); onEmailSubmit(form); });
    setTimeout(() => { const i = form.querySelector('input[type="email"]'); if (i) i.focus(); }, 50);
  }

  function renderClaimView() {
    const body = overlay.querySelector('[data-body]');
    body.innerHTML = `
      <p class="vpm__eyebrow">Code applied · adding to cart</p>
      <h2 class="vpm__h">Taking you to <em>checkout</em>…</h2>
      <p class="vpm__sub">Your <strong style="color:#c9a86c;letter-spacing:.05em;">${prize.percent}% off</strong> with code <strong style="color:#c9a86c;letter-spacing:.05em;">${prize.code}</strong> is locked in. We've also emailed it as a backup.</p>
      <div style="display:flex;align-items:center;justify-content:center;gap:10px;padding:14px 0;color:rgba(253,250,243,.75);font-size:13px;">
        <span class="vpm__spin" style="display:inline-block;width:18px;height:18px;border:2px solid rgba(201,168,108,.25);border-top-color:#c9a86c;border-radius:50%;animation:vpm-spin .9s linear infinite;"></span>
        <span data-claim-status>Adding ${CFG.product.title} to your cart…</span>
      </div>
      <a class="vpm__btn" data-claim-fallback href="/discount/${encodeURIComponent(prize.code)}?redirect=/checkout" style="display:none;">Continue to checkout →</a>
      <p class="vpm__legal">One reward per visitor. Cannot be combined with other offers.</p>
    `;
    if (!document.getElementById('vpm-spin-keyframes')) {
      const k = document.createElement('style');
      k.id = 'vpm-spin-keyframes';
      k.textContent = '@keyframes vpm-spin{to{transform:rotate(360deg)}}';
      document.head.appendChild(k);
    }
    const status = body.querySelector('[data-claim-status]');
    const fallback = body.querySelector('[data-claim-fallback]');
    // Failsafe: if anything stalls past 3.5s, surface a manual button
    const failsafe = setTimeout(() => { if (fallback) fallback.style.display = 'block'; }, 3500);

    setCartAttributes();
    addProductToCart()
      .then((ok) => {
        if (status) status.textContent = ok ? 'Applying your code…' : 'Continuing without product…';
        return applyDiscountInBackground(prize.code);
      })
      .then(() => {
        clearTimeout(failsafe);
        try {
          if (window.dataLayer) window.dataLayer.push({
            event: 'veluna_pdp_mystery_claimed',
            product_handle: CFG.product.handle,
            prize_percent: prize.percent,
            prize_code: prize.code
          });
        } catch (_) {}
        // Smooth handoff: route through /discount endpoint to guarantee cookie + cart
        window.location.href = '/discount/' + encodeURIComponent(prize.code) + '?redirect=/checkout';
      })
      .catch(() => {
        clearTimeout(failsafe);
        if (status) status.textContent = 'Almost there — tap below to continue.';
        if (fallback) fallback.style.display = 'block';
      });
  }

  function addProductToCart() {
    const variantId = CFG.product && CFG.product.variantId;
    if (!variantId) return Promise.resolve(false);
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1, properties: { _pdp_mystery_code: prize.code } }] })
    }).then((r) => r.ok).catch(() => false);
  }

  function applyDiscountInBackground(code) {
    // Hidden iframe sets the Shopify discount cookie without navigating the user
    return new Promise((resolve) => {
      try {
        const iframe = document.createElement('iframe');
        iframe.style.cssText = 'position:absolute;width:0;height:0;border:0;visibility:hidden;';
        iframe.src = '/discount/' + encodeURIComponent(code) + '?redirect=/cart.js';
        iframe.onload = () => { try { iframe.remove(); } catch(_) {} resolve(); };
        iframe.onerror = () => { try { iframe.remove(); } catch(_) {} resolve(); };
        document.body.appendChild(iframe);
        setTimeout(() => { try { iframe.remove(); } catch(_) {} resolve(); }, 4000);
      } catch (_) { resolve(); }
    });
  }

  function setCartAttributes() {
    if (!prize) return;
    try {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            'pdp_mystery_code': prize.code,
            'pdp_mystery_percent': String(prize.percent),
            'pdp_mystery_product': CFG.product.handle
          }
        })
      }).catch(() => {});
    } catch (_) {}
  }

  // ---------- build & open ----------
  function build() {
    if (overlay) return overlay;
    injectStyles();
    overlay = document.createElement('div');
    overlay.className = 'vpm__overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.innerHTML = `
      <div class="vpm__shell">
        <button class="vpm__close" data-close aria-label="Close">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
        </button>
        <div class="vpm__media">
          <span class="vpm__media-tag">Mystery reward</span>
          <img src="${(CFG.product.image||'').replace(/^\/\//,'https://')}" alt="${CFG.product.title}" loading="eager" decoding="async" onerror="this.style.display='none';this.closest('.vpm__media').classList.add('vpm__media--noimg')" />
        </div>
        <div class="vpm__body" data-body></div>
      </div>
    `;
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close('backdrop'); });
    overlay.querySelector('[data-close]').addEventListener('click', () => close('close-btn'));
    document.body.appendChild(overlay);
    return overlay;
  }

  function open(reason) {
    if (!shouldShow()) return;
    prize = pickPrize();
    if (!prize) return;
    scratched = false;
    capturedEmail = null;
    build();
    renderScratchView();
    try { window.VelunaCaptureGuard && window.VelunaCaptureGuard.notifyOpen('pdp-mystery'); } catch (_) {}
    requestAnimationFrame(() => overlay.classList.add('is-open'));
    document.body.style.overflow = 'hidden';
    isOpen = true;
    recordShown();
    try {
      if (window.dataLayer) window.dataLayer.push({
        event: 'veluna_pdp_mystery_shown',
        product_handle: CFG.product.handle,
        prize_percent: prize.percent,
        prize_code: prize.code,
        trigger: reason
      });
    } catch (_) {}
  }

  function close(reason) {
    if (!overlay) return;
    overlay.classList.remove('is-open');
    document.body.style.overflow = '';
    isOpen = false;
    setTimeout(() => { if (overlay) { overlay.remove(); overlay = null; } }, 300);
    rearm();
    try {
      if (window.dataLayer) window.dataLayer.push({
        event: 'veluna_pdp_mystery_closed',
        product_handle: CFG.product.handle,
        reason: reason
      });
    } catch (_) {}
  }

  function onScratched() {
    if (scratched) return;
    scratched = true;
    try {
      if (window.dataLayer) window.dataLayer.push({
        event: 'veluna_pdp_mystery_revealed',
        product_handle: CFG.product.handle,
        prize_percent: prize.percent,
        prize_code: prize.code
      });
    } catch (_) {}
    setTimeout(renderEmailView, 400);
  }

  function onEmailSubmit(form) {
    const input = form.querySelector('input[type="email"]');
    const errEl = form.querySelector('[data-error]');
    const btn = form.querySelector('[data-submit]');
    const email = (input.value || '').trim();
    errEl.textContent = '';
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errEl.textContent = 'Please enter a valid email.';
      return;
    }
    btn.disabled = true;
    btn.textContent = 'Sending…';
    submitToBackend(email)
      .then((res) => {
        if (res === null) throw new Error('network');
        capturedEmail = email;
        lsSet(STORAGE.captured, 'true');
        lsSet(STORAGE.won, JSON.stringify({ code: prize.code, percent: prize.percent, ts: Date.now(), product: CFG.product.handle }));
        try {
          if (window.dataLayer) window.dataLayer.push({
            event: 'veluna_lead_captured',
            source: 'pdp_mystery_offer',
            product_handle: CFG.product.handle,
            ritual_type: ritual ? ritual.id : null,
            prize_percent: prize.percent,
            prize_code: prize.code
          });
        } catch (_) {}
        renderClaimView();
      })
      .catch(() => {
        errEl.textContent = 'Something went wrong. Please try again.';
        btn.disabled = false;
        btn.textContent = 'Email my ' + prize.percent + '% off code';
      });
  }

  function submitToBackend(email) {
    const tags = ['pdp_mystery_offer', 'pdp_' + (CFG.product.handle || 'unknown'), 'prize_' + prize.percent + '_off', BACKEND.sequence];
    if (ritual) tags.push('concern_' + ritual.id);
    return fetch(BACKEND.url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: email,
        topic: 'popup_subscribe',
        source: 'veluna_pdp_mystery',
        email_sequence: BACKEND.sequence,
        popup_variant: BACKEND.popupVariant,
        tags: tags,
        fields: {
          sleep_concern: ritual ? ritual.id : '',
          ritual_type: ritual ? ritual.name : '',
          email_sequence: BACKEND.sequence,
          popup_variant: BACKEND.popupVariant,
          discount_code: prize.code,
          discount_percent: prize.percent,
          product_handle: CFG.product.handle,
          product_title: CFG.product.title,
          product_url: CFG.product.url
        },
        metadata: {
          page: window.location.pathname,
          referrer: document.referrer || '',
          user_agent: (navigator && navigator.userAgent) || ''
        }
      })
    }).catch(() => null);
  }

  // ---------- scratch canvas ----------
  function initScratch() {
    const wrap = overlay.querySelector('[data-scratch-wrap]');
    const canvas = overlay.querySelector('[data-scratch-canvas]');
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = Math.max(1, window.devicePixelRatio || 1);
    const rect = wrap.getBoundingClientRect();
    canvas.width = Math.floor(rect.width * dpr);
    canvas.height = Math.floor(rect.height * dpr);
    ctx.scale(dpr, dpr);

    const grad = ctx.createLinearGradient(0, 0, rect.width, rect.height);
    grad.addColorStop(0, '#d8b97a');
    grad.addColorStop(0.5, '#c9a86c');
    grad.addColorStop(1, '#a8884a');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, rect.width, rect.height);
    ctx.fillStyle = 'rgba(26,31,53,0.55)';
    ctx.font = 'bold 14px Inter, system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('SCRATCH HERE', rect.width / 2, rect.height / 2 - 8);
    ctx.font = '11px Inter, system-ui, sans-serif';
    ctx.fillStyle = 'rgba(26,31,53,0.4)';
    ctx.fillText('Your mystery reward awaits', rect.width / 2, rect.height / 2 + 12);

    let drawing = false;
    let revealed = false;
    let lastPoint = null;
    let scratchStartedAt = 0;

    function pos(e) {
      const r = canvas.getBoundingClientRect();
      const t = e.touches && e.touches[0];
      const cx = (t ? t.clientX : e.clientX) - r.left;
      const cy = (t ? t.clientY : e.clientY) - r.top;
      return { x: cx, y: cy };
    }
    function scratchAt(p) {
      ctx.globalCompositeOperation = 'destination-out';
      // Draw a thick line from the previous point so fast drags actually fill the path
      if (lastPoint) {
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.lineWidth = 60;
        ctx.beginPath();
        ctx.moveTo(lastPoint.x, lastPoint.y);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
      }
      ctx.beginPath();
      ctx.arc(p.x, p.y, 32, 0, Math.PI * 2);
      ctx.fill();
      lastPoint = p;
    }
    function pctScratched() {
      const img = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
      let cleared = 0;
      const step = 8 * 4;
      const total = img.length / step;
      for (let i = 3; i < img.length; i += step) {
        if (img[i] === 0) cleared++;
      }
      return cleared / total;
    }
    function updateHint() {
      const hint = overlay && overlay.querySelector('[data-hint]');
      if (hint && !hint.dataset.scratched) {
        hint.dataset.scratched = '1';
        hint.textContent = 'Keep going — you’re almost there…';
      }
    }
    function fireConfetti() {
      const wrap = overlay && overlay.querySelector('[data-scratch-wrap]');
      if (!wrap) return;
      wrap.classList.add('is-revealed');
      const burst = document.createElement('div');
      burst.className = 'vpm__confetti';
      const colors = ['#c9a86c', '#e8c98a', '#fdfaf3', '#8b6f3f'];
      for (let i = 0; i < 22; i++) {
        const s = document.createElement('span');
        const ang = Math.random() * Math.PI * 2;
        const dist = 80 + Math.random() * 120;
        s.style.left = (40 + Math.random() * 20) + '%';
        s.style.top = (45 + Math.random() * 10) + '%';
        s.style.background = colors[i % colors.length];
        s.style.setProperty('--dx', (Math.cos(ang) * dist).toFixed(0) + 'px');
        s.style.setProperty('--dy', (Math.sin(ang) * dist - 40).toFixed(0) + 'px');
        s.style.animationDelay = (Math.random() * 0.15) + 's';
        burst.appendChild(s);
      }
      wrap.appendChild(burst);
      try { if (navigator && navigator.vibrate) navigator.vibrate([30, 40, 30]); } catch (_) {}
      setTimeout(() => { try { burst.remove(); } catch (_) {} }, 1800);
    }
    function maybeAutoReveal() {
      if (revealed) return;
      const pct = pctScratched();
      if (pct > 0.04) updateHint();
      // Auto-reveal once a meaningful patch is cleared
      if (pct > 0.12) {
        triggerReveal();
      }
    }
    function triggerReveal() {
      if (revealed) return;
      revealed = true;
      fireConfetti();
      canvas.style.transition = 'opacity .55s ease';
      canvas.style.opacity = '0';
      setTimeout(() => { try { canvas.style.display = 'none'; } catch(_) {} onScratched(); }, 700);
    }
    function onRelease() {
      drawing = false;
      lastPoint = null;
      if (revealed) return;
      const pct = pctScratched();
      const dwell = Date.now() - scratchStartedAt;
      // Failsafe: if the user has scratched anything substantive (>6%) AND
      // released their finger after a real interaction (>250ms), reveal.
      if (pct > 0.06 && dwell > 250) {
        triggerReveal();
        return;
      }
      // Last-resort failsafe: after the FIRST release, if any scratching occurred,
      // schedule auto-reveal in 1.2s so the popup never stalls.
      if (pct > 0.02 && !canvas.dataset.failsafeArmed) {
        canvas.dataset.failsafeArmed = '1';
        setTimeout(() => {
          if (!revealed && pctScratched() > 0.02) triggerReveal();
        }, 1200);
      }
    }
    canvas.addEventListener('mousedown', (e) => { drawing = true; scratchStartedAt = Date.now(); lastPoint = null; scratchAt(pos(e)); });
    canvas.addEventListener('mousemove', (e) => { if (drawing) { scratchAt(pos(e)); maybeAutoReveal(); } });
    window.addEventListener('mouseup', onRelease);
    canvas.addEventListener('touchstart', (e) => { drawing = true; scratchStartedAt = Date.now(); lastPoint = null; scratchAt(pos(e)); e.preventDefault(); }, { passive: false });
    canvas.addEventListener('touchmove', (e) => { if (drawing) { scratchAt(pos(e)); maybeAutoReveal(); } e.preventDefault(); }, { passive: false });
    canvas.addEventListener('touchend', onRelease);
    canvas.addEventListener('touchcancel', onRelease);
  }

  // ---------- triggers ----------
  function shouldShow() {
    if (isOpen) return false;
    /* VELUNA_CAPTURE_GUARD: purchase configuration and in-flight purchases
       outrank email capture. Policy lives in veluna-capture-guard.js. */
    if (window.VelunaCaptureGuard && !window.VelunaCaptureGuard.mayShow('pdp-mystery')) return false;
    if (hasWon()) return false;
    if (inCooldown()) return false;
    return true;
  }
  function attemptOpen(reason) {
    if (!shouldShow()) return;
    open(reason);
  }
  // Resilient exit-intent: mouseleave listener stays bound for the session.
  // After close, only the cooldown gate (default 60s) throttles re-fire.
  // `hasWon()` permanently suppresses once a code is claimed. A short
  // re-arm latch prevents flickering double-fires from the same mouse-out.
  let triggersInstalled = false;
  let exitIntentArmed = true;
  function onMouseLeave(e) {
    if (!exitIntentArmed) return;
    if (e.clientY > 0) return;
    if (!shouldShow()) return;
    exitIntentArmed = false;
    attemptOpen('exit-intent');
    setTimeout(() => { exitIntentArmed = true; }, 1500);
  }
  function installTriggers() {
    if (triggersInstalled) return;
    triggersInstalled = true;
    if (dwellTimer) clearTimeout(dwellTimer);
    dwellTimer = setTimeout(() => attemptOpen('dwell'), (CFG.dwellSeconds || 15) * 1000);
    document.addEventListener('mouseleave', onMouseLeave);
    var depthFired = false;
    window.addEventListener('scroll', function () {
      if (depthFired || !shouldShow()) return;
      var doc = document.documentElement;
      var max = (doc.scrollHeight - doc.clientHeight);
      if (max > 0 && (window.scrollY || 0) / max > 0.5) { depthFired = true; attemptOpen('scroll-depth'); }
    }, { passive: true });
    // Mobile exit signals
    var isTouch = !!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden' && exitIntentArmed && shouldShow()) {
        exitIntentArmed = false;
        attemptOpen('mobile-visibility');
        setTimeout(() => { exitIntentArmed = true; }, 1500);
      }
    });
    if (isTouch) {
      /* HISTORY TRAPPING REMOVED 2026-09-05.
         This previously pushed a dummy history entry on load and RE-PUSHED it
         on popstate, so a mobile visitor's first Back press did not leave the
         page — it only opened this popup and put them back where they were.
         Trapping navigation to increase popup exposure is not acceptable on
         traffic we pay for. Mobile recovery is preserved through legitimate
         page-state signals only: `mobile-visibility` (visibilitychange, below)
         and `mobile-scroll-up`. No history manipulation remains. */
      var maxY = 0, scrollUpFired = false;
      window.addEventListener('scroll', () => {
        if (scrollUpFired || !shouldShow()) return;
        var y = window.scrollY || 0;
        if (y > maxY) maxY = y;
        if (maxY > 700 && y < maxY - 280) {
          scrollUpFired = true;
          attemptOpen('mobile-scroll-up');
        }
      }, { passive: true });
    }
  }
  function rearm() {
    exitIntentArmed = true;
    if (dwellTimer) clearTimeout(dwellTimer);
    dwellTimer = setTimeout(() => attemptOpen('dwell-rearm'), (CFG.dwellSeconds || 35) * 1000);
  }

  // ---------- init ----------
  function reapplyStoredDiscount() {
    // If user already won previously, ensure cookie + cart attribute are still set
    try {
      const stored = ls(STORAGE.won);
      if (!stored) return;
      const data = JSON.parse(stored);
      if (!data || !data.code) return;
      // Refresh cookie via hidden iframe (cheap) and re-apply cart attrs
      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:absolute;width:0;height:0;border:0;visibility:hidden;';
      iframe.src = '/discount/' + encodeURIComponent(data.code) + '?redirect=/cart.js';
      iframe.onload = () => { try { iframe.remove(); } catch(_) {} };
      iframe.onerror = () => { try { iframe.remove(); } catch(_) {} };
      document.body.appendChild(iframe);
      setTimeout(() => { try { iframe.remove(); } catch(_) {} }, 4000);
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            'pdp_mystery_code': data.code,
            'pdp_mystery_percent': String(data.percent || ''),
            'pdp_mystery_product': data.product || ''
          }
        })
      }).catch(() => {});
    } catch (_) {}
  }

  function init() {
    if (hasWon()) {
      // Already claimed — keep cookie + cart attrs fresh so checkout always sees the discount
      reapplyStoredDiscount();
      installCheckoutInterceptor();
      return;
    }
    installTriggers();
    // Also intercept (in case discount was won on another page in this session)
    installCheckoutInterceptor();
  }

  function getStoredPrize() {
    try {
      const stored = ls(STORAGE.won);
      if (!stored) return null;
      const d = JSON.parse(stored);
      return (d && d.code) ? d : null;
    } catch (_) { return null; }
  }

  function installCheckoutInterceptor() {
    // Any click on a checkout link/button routes through /discount/CODE?redirect=/checkout
    // so the discount cookie + URL param are both set, guaranteeing checkout sees it.
    if (window.__velunaCheckoutInterceptorInstalled) return;
    window.__velunaCheckoutInterceptorInstalled = true;
    document.addEventListener('click', function (e) {
      const prize = getStoredPrize();
      if (!prize) return;
      const t = e.target && e.target.closest && e.target.closest('a[href*="/checkout"], a[href*="/cart/checkout"], button[name="checkout"], [data-checkout-button], [data-action="checkout"]');
      if (!t) return;
      // Avoid recursion
      if (t.getAttribute('data-veluna-discount-applied') === '1') return;
      e.preventDefault();
      e.stopPropagation();
      t.setAttribute('data-veluna-discount-applied', '1');
      const target = (t.tagName === 'A' && t.getAttribute('href')) ? t.getAttribute('href') : '/checkout';
      window.location.href = '/discount/' + encodeURIComponent(prize.code) + '?redirect=' + encodeURIComponent(target);
    }, true);

    // Also intercept programmatic checkout form submits
    document.addEventListener('submit', function (e) {
      const prize = getStoredPrize();
      if (!prize) return;
      const form = e.target;
      const action = (form && form.getAttribute && form.getAttribute('action')) || '';
      if (!/\/checkout|\/cart\/checkout/.test(action)) return;
      if (form.getAttribute('data-veluna-discount-applied') === '1') return;
      e.preventDefault();
      form.setAttribute('data-veluna-discount-applied', '1');
      window.location.href = '/discount/' + encodeURIComponent(prize.code) + '?redirect=' + encodeURIComponent(action || '/checkout');
    }, true);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.__velunaMysteryTest = function () {
    try { localStorage.removeItem(STORAGE.won); localStorage.removeItem(STORAGE.lastShown); } catch (_) {}
    open('manual-test');
  };
})();
